/*
 * Basic contact modal is in charge of all the process of
 * validating and sending the basic contact form to the server.
 * Additionally, it loads correctly the modals that should be
 * loaded after sending the form.
 * This file requires VMasker and formValidator to work properly.
 */
(function basicContactModal(global) {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};

  if (!global.VMasker || !global.formValidator) {
    return;
  }

  const previousBasicContactModal = global.NewHomeSource.BasicContactModal;

  /**
   * @constructs NewHomeSource.BasicContactModal
   */
  function BasicContactModal() {
  }

  /**
   * @private
   * CheckPhoneMask: Checks the phone input is following the mask pattern.
   */
  function CheckPhoneMask(phoneInput) {
    global.VMasker(phoneInput).maskPattern('(999) 999-9999');
  }

  /**
   * CloseError: Manages the closing of the error message on screen.
   * @param {Object} event result of the click on the page.
   */
  function CloseError(event) {
    const target = event.target;
    target.textContent = '';
    target.classList.remove('basic-summary-errors');
  }

  /**
   * This method will extract the necessary properties for the event in the SRP.
   */
  function getProperties(event, eventForSegment) {
    const commonProperties = document.querySelector('#data-commonProperties_' + event.dataset.card_id);
    if (!commonProperties || !commonProperties.dataset) {
      return eventForSegment;
    }
    const propertiesForSegment = Object.assign({}, eventForSegment, commonProperties.dataset);
    return propertiesForSegment;
  }

  /**
   * Returns a command object to be used to get the recommended communities.
   * @param {FormData} data - The form data containing the necessary fields.
   * @returns {Object} - The recommended command object.
   */
  function getRecommendedCommunitiesCommand(data) {
    const planId = Number(data.get('PlanId'));
    const specId = Number(data.get('SpecId'));
    const listingId = Number(data.get('ListingId'));
    const communityId = Number(data.get('CommunityId'));
    const mailAddress = data.get('MailAddress') || data.get('email');
    const leadType = data.get('Type');

    const command = {
      CommunityId: communityId,
      ListingId: listingId,
      MailAddress: mailAddress,
      Type: leadType,
    };

    if (specId) {
      command.SpecId = specId;
    } else if (planId) {
      command.PlanId = planId;
    }

    return command;
  }

  /**
   * Retrieves recommended communities based on the provided data.
   * @param {Object} data - The data used to generate the recommended communities.
   * @returns {Promise<string>} - A promise that resolves with the recommended communities.
   */
  async function getRecommendedCommunities(data) {
    const recommendedCommand = getRecommendedCommunitiesCommand(data);

    const recommended = await fetch('/ajax/leads/getrecommendedcommunities', {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({
        leadCommand: {
          ...recommendedCommand,
        },
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    }).then((response) => response.text());

    return recommended;
  }

  /**
   * Shows the recommendations modal.
   * @param {string} recommended - The recommended content to display in the modal.
   * @param {HTMLElement} basicForm - The basic form element that triggered the lead.
   */
  function showRecomendationsModal(recommended, basicForm) {
    const modalExists = document.getElementById('#modal-content');

    if (!modalExists) {
      const templateElement = document.querySelector('[data-recommendations-template]');
      const template = document.createRange().createContextualFragment(templateElement.innerHTML);

      if (window.NewHomeSource.ModalLoader) {
        window.NewHomeSource.ModalLoader.createModal(template);
      }
    } else {
      const formContainer = basicForm.closest('.basic__form-wrapper');
      formContainer.setAttribute('hidden', 'hidden');
    }

    if (recommended !== '') {
      // if the lead was requested from the on-page brochure, create the modal.

      const recommendationsModal = document.getElementById('nhs_BasicModalRecommendations');
      const container = recommendationsModal.querySelector('.recommendations__container');
      const submitButton = basicForm.querySelector('[data-submit-form]');

      recommendationsModal.removeAttribute('hidden');
      container.innerHTML = recommended;
      document.body.dispatchEvent(new CustomEvent('recommededModalReady', {
        detail: {
          modal: recommendationsModal
        }
      }));

      const ga4Event = {
        eventName: 'direc_section_view',
        eventType: 'GA4Event',
        params: {
          position: submitButton.dataset.ga4IdentityPosition,
          button_name: submitButton.dataset.ga4IdentityButton_name,
        }
      };

      document.body.dispatchEvent(new CustomEvent('trackOnDemandEvent', {
        detail: {
          data: ga4Event,
        }
      }));
    } else {
      // emit a custom event to show the thank you modal
      document.body.dispatchEvent(new CustomEvent('showThankYouModal'));
    }

    const modalBackground = document.querySelector('.modal_background');
    modalBackground.classList.add('modal__background-dark');

    const modalContainer = document.querySelector('#modal-content');
    modalContainer.classList.add('modal__container--recommendations');
  }

  /**
   * Toggles the visibility of a modal element by adding or removing the 'hidden' attribute.
   * @param {HTMLElement} modal - The modal element to toggle.
   */
  function toggleModal(modal) {
    modal.toggleAttribute('hidden');
  }

  /**
   * Gets an array with the recommended communities information selected by the user.
   * @param {Object} formData with the form inputs to be submited.
   */
  function getSelectedRecommendedCommunities() {
    return Array.from(document.querySelectorAll('[data-recommendation-check]'))
      .filter((recommendation) => recommendation.checked)
      .map((recommendation) => JSON.parse(recommendation.value));
  }

  /**
   *
   * Validates if there's at least 1 recommendation selected. If that's not the case,
   * disables the brochure button.
   */
  function onRecommendationChecked() {
    const submitButton = document.querySelector('[data-recommended-button]');
    const checkedRecos = document.querySelectorAll('[data-recommendation-check]:checked');
    if (checkedRecos.length === 0) {
      submitButton.disabled = true;
      return;
    }

    submitButton.disabled = false;
  }

  /**
   * Returns a command object to be used to get the recommended communities.
   * @param {FormData} data - The form data containing the necessary fields.
   * @returns {Object} - The recommended command object.
   */
  function getRecommendedCommunitiesSubmissionCommand(data) {
    const name = data.get('Name') || '';
    const firstName = name.split(' ')[0] || '';
    const lastName = name.split(' ')[1] || '';
    const recoComms = getSelectedRecommendedCommunities();

    return {
      CommunityId: data.get('CommunityId'),
      FromPage: data.get('FromPage'),
      Zip: data.get('ZipCode'),
      Phone: data.get('UserPhoneNumber'),
      BuilderId: data.get('BuilderId'),
      Email: data.get('MailAddress') || data.get('email') || '',
      MarketId: data.get('MarketId'),
      Type: 'postCommunityBrochureBasic',
      FirstName: firstName,
      LastName: lastName,
      RecoComms: recoComms,
      Guid: window.NewHomeSource.GuidGenerator.leadGuid,
    };
  }

  /**
   * The function dispatches a custom event named 'onRecommendationSend' with the form data as detail.
   * @param form - The `form` parameter in the `dispatchOnRecommendationSend` function is an object
   * that contains data related to a form.
   */
  function dispatchOnRecommendationSend(form) {
    document.body.dispatchEvent(new CustomEvent('onRecommendationSend', {
      detail: { form }
    }));
  }

  /**
   * Attaches event listeners to the recommendations modal.
   * @function
   * @returns {void}
   */
  function bindEvents(data) {
    const recommendationsModal = document.querySelector('[data-basic-recommendations-modal]');
    const inputRecommended = recommendationsModal.querySelector('[data-direct-lead-recommendation]');

    const skipButton = recommendationsModal.querySelector('[data-skip-button]');
    skipButton.setAttribute('data-ga4-event-position', data.get('GenerateLeadFrom'));
    skipButton.setAttribute('data-ga4-event-button_name', data.get('ButtonNameSource'));
    skipButton.addEventListener('click', () => {
      // hide the recommendations modal
      toggleModal(recommendationsModal);

      // emit a custom event to show the thank you modal
      document.body.dispatchEvent(new CustomEvent('showThankYouModal'));
    });

    const closeButton = recommendationsModal.querySelector('[data-modal-close-element]');
    closeButton.setAttribute('data-ga4-event-position', data.get('GenerateLeadFrom'));
    closeButton.setAttribute('data-ga4-event-button_name', data.get('ButtonNameSource'));

    const recommendedElements = document.querySelectorAll('[data-recommendation-check]');
    recommendedElements.forEach((elem) => elem.addEventListener('click', onRecommendationChecked.bind(data)));

    if (inputRecommended) {
      inputRecommended.setAttribute('data-ga4-position', data.get('GenerateLeadFrom'));
      inputRecommended.setAttribute('data-ga4-org_button_name', data.get('ButtonNameSource'));
    }

    const submitButton = recommendationsModal.querySelector('[data-recommended-button]');
    submitButton.addEventListener('click', async () => {
      const recommendationsSubmissionCommand = getRecommendedCommunitiesSubmissionCommand(data);

      // hide the recommendations modal
      toggleModal(recommendationsModal);

      // emit a custom event to show the thank you modal
      document.body.dispatchEvent(new CustomEvent('showThankYouModal'));
      const communititesChecked = getSelectedRecommendedCommunities();
      communititesChecked.forEach(dispatchOnRecommendationSend.bind(null, recommendationsModal));

      // we use a shoot and forget request to send the recommended lead data to the server
      fetch('/ajax/leads/recommendedcommunitysubmission/', {
        method: 'POST',
        body: JSON.stringify({
          ...recommendationsSubmissionCommand,
        }),
        headers: {
          'Content-Type': 'application/json',
        },
      });
    });
  }

  /**
   * @private
   * handleBasicForm: Sends the basic form information.
   * @param {object} event result of the click on the page.
   */
  async function handleBasicForm(basicForm) {
    // lazy load the modals bundle.
    const bundle = document.querySelector('#basicModalsBundle').value;

    if (bundle && window.NHS && window.NHS.loadCss) {
      window.NHS.loadCss(bundle);
    }

    const submitButton = basicForm.querySelector('[data-submit-form]');

    if (await global.formValidator.validateForm(basicForm)) {
      document.body.dispatchEvent(new CustomEvent('valid-lead', {
        detail: {
          form: basicForm,
          formButton: submitButton,
        }
      }));

      window.NewHomeSource.GuidGenerator.leadGuid = window.NewHomeSource.GuidGenerator.generateGUID();
      const data = new FormData(basicForm);
      data.append('Guid', window.NewHomeSource.GuidGenerator.leadGuid);

      // we use a shoot and forget request to send the lead data to the server
      fetch(basicForm.action, {
        method: 'POST',
        body: data
      });

      const recommended = await getRecommendedCommunities(data);
      data.append('GenerateLeadFrom', submitButton.dataset.ga4IdentityPosition);
      data.append('ButtonNameSource', submitButton.dataset.ga4IdentityButton_name);
      showRecomendationsModal(recommended, basicForm);
      bindEvents(data);

      // allow users to submit the form again if possible.
      submitButton.removeAttribute('disabled');

      let eventForSegment = {
        event: 'Lead Modal Form Submitted',
        eventType: 'segmentEvent',
        button_action: submitButton.dataset.button_action,
        community_id: submitButton.dataset.community,
      };

      if (submitButton.dataset.card_id) {
        eventForSegment = getProperties(submitButton, eventForSegment);
      }

      document.body.dispatchEvent(new CustomEvent('trackOnDemandEvent', {
        detail: { data: eventForSegment }
      }));

      const sentErrorElement = document.querySelector('[data-sent-error-message]');
      if (sentErrorElement) {
        sentErrorElement.textContent = sentErrorElement.getAttribute('data-sent-error-message');
        sentErrorElement.classList.add('basic-summary-errors');
        submitButton.removeAttribute('disabled');
        sentErrorElement.addEventListener('click', CloseError);
      }

      // reset the modal loader flag to prevent the recommendations or thank you modal from being loaded again.
      // we need to reset the flag here to allow the form to be refetched from the backend.
      if (window.NewHomeSource.ModalLoader) {
        window.NewHomeSource.ModalLoader.resetIsModalLoadedFlag();
      }
    }
  }

  /**
   * @private
   * sendBasicLead: function to send any basic lead.
   * @param {Event} event The click or touch event.
  */
  async function sendBasicLead(event) {
    event.preventDefault();
    if (!global.NewHomeSource.TrustedForm) {
      return;
    }
    const form = event.target.closest('[data-form]') || document.querySelector('[data-form]');
    const submitButton = form.querySelector('[data-submit-form]');
    submitButton.setAttribute('disabled', 'disabled');

    const validations = form ? await global.formValidator.validateForm(form) : false;

    if (validations) {
      const functionToPerform = handleBasicForm;

      global.NewHomeSource.TrustedForm.load(() => {
        functionToPerform(form);
      });
    }
    submitButton.removeAttribute('disabled');
  }

  /**
   * @private
   * SubmitButtonClicked: tracks the click event when form submit button is clicked.
   * @param {object} event result of the click on the page.
   */
  async function SubmitButtonClicked(event) {
    if (event.target && event.target.dataset.submitForm !== undefined) {
      CheckPhoneMask(event.target.form.querySelector('[data-phone-input]'));
      await sendBasicLead(event);
    }
  }

  /**
   * @private
   * HandleClicksEvent: Checks the click event on the document body and executes its respective method.
   * @param {object} event result of the click on the page.
   */
  async function HandleClicksEvent(event) {
    if (event.target && event.target.dataset.submitForm !== undefined) {
      await SubmitButtonClicked(event);
    }
  }

  /**
   * @private
   * HandleFocusInEvent: Checks the focus in events on the document body and executes its respective method.
   * @param {object} event result of the click on the page.
   */
  function HandleFocusInEvent(event) {
    if (event.target && event.target.getAttribute('name') === 'UserPhoneNumber') {
      CheckPhoneMask(event.target);
    }
  }

  /**
   * @public
   *
   * noConflict: Returns control of NewHomeSource.BasicContactModal to the previous script.
   */
  BasicContactModal.noConflict = function noConflict() {
    global.NewHomeSource.BasicContactModal = previousBasicContactModal;
    return BasicContactModal;
  };

  global.NewHomeSource.BasicContactModal = BasicContactModal;
  document.body.addEventListener('click', HandleClicksEvent);
  document.body.addEventListener('focusin', HandleFocusInEvent);
}(window));
